import React, { useRef, useState, useEffect } from "react";
import { 
  MessageSquare, 
  Search, 
  ListFilter, 
  RotateCcw, 
  Globe,
  Download,
  Filter,
  Calendar,
  DollarSign
} from "lucide-react";
import SocialActivationModal from "./components/SocialActivationModal.jsx";
import SocialReceipt from "./components/SocialReceipt";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getAgentSocialOrders } from "../../../services/socialProductsService";
import { handleSetSearch } from "../../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable.jsx";
import { toast } from "react-toastify";

function SocialActivation() {
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [refetch, setRefetch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    startDate: "",
    endDate: "",
    receiver: "",
    minAmount: "",
    maxAmount: "",
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const closeActivationModal = () => setActivationModalOpen(false);

  const { data, isLoading, isError, error, refetch: refetchData } = useQuery({
    queryKey: ["socialOrders", filter, refetch],
    queryFn: () => getAgentSocialOrders(userInfo?.uid, filter),
  });

  const handleRefetch = () => {
    setRefetch(prev => !prev);
    toast.success("Data refreshed!");
  };

  const handleActivationSuccess = () => {
    setActivationModalOpen(false);
    handleRefetch();
    toast.success("Social product activation initiated successfully!");
  };

  const handleSearchChange = (value) => {
    setFilter(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilter(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilter(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilter(prev => ({ 
      ...prev, 
      status: status === "all" ? "" : status,
      page: 1 
    }));
  };

  const handleDateFilter = (start, end) => {
    setFilter(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      page: 1
    }));
  };

  const handleAmountFilter = (min, max) => {
    setFilter(prev => ({
      ...prev,
      minAmount: min,
      maxAmount: max,
      page: 1
    }));
  };

  const handleSort = (sortBy, sortOrder) => {
    setFilter(prev => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1
    }));
  };

  const handleClearFilters = () => {
    setFilter({
      search: "",
      limit: 10,
      page: 1,
      status: "",
      startDate: "",
      endDate: "",
      receiver: "",
      minAmount: "",
      maxAmount: "",
      sortBy: "createdAt",
      sortOrder: "DESC",
    });
    setShowFilters(false);
    toast.info("Filters cleared!");
  };

  const handleExportData = () => {
    toast.info("Export feature coming soon!");
  };

  const columns = [
    { 
      key: "txnNumber", 
      label: "Transaction ID",
      sortable: true,
      render: (item) => (
        <div className="font-mono text-sm text-blue-600">
          {item?.txnNumber || "-"}
        </div>
      )
    },
    { 
      key: "receiver", 
      label: "Social Account",
      sortable: true,
      render: (item) => {
        const getPlatformIcon = (account) => {
          if (!account) return "📱";
          account = account.toLowerCase();
          if (account.includes('facebook') || account.includes('fb')) return '🔵';
          if (account.includes('instagram') || account.includes('ig')) return '🌈';
          if (account.includes('whatsapp') || account.includes('wa')) return '🟢';
          if (account.includes('twitter') || account.includes('x')) return '🐦';
          if (account.includes('tiktok')) return '🎵';
          if (account.includes('telegram') || account.includes('tg')) return '✈️';
          if (account.includes('snapchat')) return '👻';
          if (account.includes('youtube') || account.includes('yt')) return '▶️';
          return '📱';
        };

        return (
          <div className="flex items-center gap-2">
            <span className="text-lg">{getPlatformIcon(item?.receiver)}</span>
            <div>
              <div className="font-medium truncate max-w-[150px]">
                {item?.receiver || "-"}
              </div>
              {item?.operator && (
                <div className="text-xs text-gray-500">{item.operator}</div>
              )}
            </div>
          </div>
        );
      }
    },
    { 
      key: "amount", 
      label: "Amount",
      sortable: true,
      render: (item) => (
        <div className="font-semibold">
          {parseFloat(item?.amount || 0).toFixed(2)} AFN
        </div>
      )
    },
    { 
      key: "totalAmount", 
      label: "Total",
      sortable: true,
      render: (item) => (
        <div className="font-semibold text-green-600">
          {parseFloat(item?.totalAmount || item?.amount || 0).toFixed(2)} AFN
        </div>
      )
    },
    {
      key: "product",
      label: "Product",
      render: (item) => (
        <div className="max-w-[180px]">
          <div className="font-medium truncate">
            {item?.productDetails?.productName?.en || item?.productDetails?.productName || "-"}
          </div>
          {item?.productDetails?.description && (
            <div className="text-xs text-gray-500 truncate">
              {item.productDetails.description}
            </div>
          )}
        </div>
      )
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (item) => {
        const statusColors = {
          succeeded: { bg: "bg-green-100", text: "text-green-800" },
          pending: { bg: "bg-yellow-100", text: "text-yellow-800" },
          processing: { bg: "bg-blue-100", text: "text-blue-800" },
          failed: { bg: "bg-red-100", text: "text-red-800" },
          cancelled: { bg: "bg-gray-100", text: "text-gray-800" }
        };
        
        const status = item?.status || 'pending';
        const colors = statusColors[status] || statusColors.pending;
        
        return (
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center justify-center w-fit min-w-[80px] ${colors.bg} ${colors.text}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </div>
        );
      }
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (item) => (
        <div className="text-sm">
          <div className="font-medium">
            {item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}
          </div>
          <div className="text-xs text-gray-500">
            {item?.createdAt ? new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
          </div>
        </div>
      )
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <div className="flex gap-2">
          <button 
            onClick={() => {
              setSelectedTransaction(item);
              setReceiptOpen(true);
            }}
            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1.5 border border-blue-600 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
          >
            <MessageSquare size={14} />
            Receipt
          </button>
          {item.status === 'pending' && (
            <button 
              onClick={() => {
                // Handle cancel action
                toast.info("Cancel feature coming soon!");
              }}
              className="text-red-600 hover:text-red-800 text-sm px-3 py-1.5 border border-red-600 rounded hover:bg-red-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )
    }
  ];

  const customEmptyState = (
    <div className="w-full min-h-[300px] flex flex-col items-center justify-center py-10">
      <div className="relative">
        <div className="w-40 h-40 flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-full">
          <MessageSquare size={64} className="text-gray-400" />
        </div>
        <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
          <Globe size={20} className="text-blue-600" />
        </div>
      </div>
      <p className="mt-6 text-lg font-medium text-gray-700">No Social Activations Yet</p>
      <p className="mt-2 text-gray-500 text-center max-w-md">
        You haven't activated any social products yet. Start by activating your first social media package.
      </p>
      <button
        onClick={() => setActivationModalOpen(true)}
        className="mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
      >
        <MessageSquare size={18} />
        Activate First Social Product
      </button>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[300px] flex flex-col items-center justify-center py-10">
      <div className="w-32 h-32 flex items-center justify-center bg-red-50 rounded-full">
        <MessageSquare size={48} className="text-red-400" />
      </div>
      <p className="mt-6 text-lg font-medium text-gray-700">Failed to Load Data</p>
      <div className="mt-2 text-sm text-red-600 mb-4 text-center max-w-md">
        {error?.message || "An error occurred while loading your social activations"}
      </div>
      <div className="flex gap-3">
        <button
          onClick={handleRefetch}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
        >
          <RotateCcw size={16} />
          Retry
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );

  const stats = data?.data?.pagination || {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    itemsPerPage: 10
  };


  const calculateStats = () => {
    const orders = data?.data?.orders || [];
    const succeeded = orders.filter(o => o.status === 'succeeded').length;
    const pending = orders.filter(o => o.status === 'pending').length;
    const totalAmount = orders.reduce((sum, o) => sum + parseFloat(o.totalAmount || 0), 0);
    const avgAmount = orders.length > 0 ? totalAmount / orders.length : 0;

    return {
      total: orders.length,
      succeeded,
      pending,
      totalAmount: totalAmount.toFixed(2),
      avgAmount: avgAmount.toFixed(2)
    };
  };

  const summaryStats = calculateStats();

  return (
    <div className="w-full px-5 py-4 min-h-screen">
      <SocialActivationModal 
        open={activationModalOpen} 
        onClose={closeActivationModal} 
        onSuccess={handleActivationSuccess} 
      />
      
      <SocialReceipt
        transaction={selectedTransaction} 
        isOpen={receiptOpen} 
        onClose={() => setReceiptOpen(false)} 
      />
      

      <div className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Social Product Activation</h1>
            <p className="text-gray-600">Manage and track your social media product activations</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportData}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700">Total Activations</p>
              <p className="text-2xl font-bold text-blue-900">{summaryStats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
              <MessageSquare size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700">Successful</p>
              <p className="text-2xl font-bold text-green-900">{summaryStats.succeeded}</p>
            </div>
            <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
              <MessageSquare size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700">Pending</p>
              <p className="text-2xl font-bold text-yellow-900">{summaryStats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-200 rounded-full flex items-center justify-center">
              <MessageSquare size={24} className="text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700">Total Amount</p>
              <p className="text-2xl font-bold text-purple-900">{summaryStats.totalAmount} AFN</p>
            </div>
            <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
              <DollarSign size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mt-6">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="max-w-[400px] w-full h-[44px] relative dark:text-white rounded-md border border-[#E4E7EC] dark:border-gray-500">
            <input
              onChange={(e) => {
                handleSetSearch(handleSearchChange, e.target.value, searchRef);
              }}
              type="text"
              placeholder="Search by account, transaction ID, or product..."
              value={filter.search}
              className="bg-transparent dark:placeholder-gray-200 h-full pl-10 transition-all duration-300 absolute left-0 top-0 px-3 py-2 w-full outline-none"
            />
            <Search size={18} className="absolute top-[12px] left-[8px]" />
            {filter.search && (
              <button
                onClick={() => handleSearchChange("")}
                className="absolute top-[12px] right-[8px] text-gray-500 hover:text-red-500"
              >
                ×
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center gap-2 whitespace-nowrap"
          >
            <Filter size={16} />
            Filters
            {Object.values(filter).filter(v => v && v !== "createdAt" && v !== "DESC").length > 2 && (
              <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {Object.values(filter).filter(v => v && v !== "createdAt" && v !== "DESC").length - 2}
              </span>
            )}
          </button>
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={handleRefetch}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center gap-2"
          >
            <RotateCcw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setActivationModalOpen(true)}
            className="bg-primary from-blue-600 to-purple-600 px-4 py-2 rounded-md text-white text-sm hover:from-blue-700 hover:to-purple-700 flex items-center gap-2"
          >
            <MessageSquare size={16} />
            Activate Social Product
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-800">Advanced Filters</h3>
            <button
              onClick={handleClearFilters}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Clear All Filters
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filter.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Status</option>
                <option value="succeeded">Succeeded</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filter.startDate}
                  onChange={(e) => handleDateFilter(e.target.value, filter.endDate)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <input
                  type="date"
                  value={filter.endDate}
                  onChange={(e) => handleDateFilter(filter.startDate, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount Range</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filter.minAmount}
                  onChange={(e) => handleAmountFilter(e.target.value, filter.maxAmount)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filter.maxAmount}
                  onChange={(e) => handleAmountFilter(filter.minAmount, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={filter.sortBy}
                onChange={(e) => handleSort(e.target.value, filter.sortOrder)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="createdAt">Date Created</option>
                <option value="amount">Amount</option>
                <option value="status">Status</option>
                <option value="receiver">Account</option>
              </select>
            </div>
          </div>
        </div>
      )}


      <div className="mt-6">
        <ARHCustomDataTable
          data={data?.data?.orders || []}
          columns={columns}
          meta={stats}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700"
          headerClassName="bg-gray-50"
          showPagination={true}
          showEntries={true}
          showSearch={false}
        />
      </div>
    </div>
  );
}

export default SocialActivation;